// src/enums.ts
import { z } from "zod";
var ROLES = [
  "SUPER_ADMIN",
  "MERCHANT_OWNER",
  "MERCHANT_ADMIN",
  "MERCHANT_SUPPORT",
  "VIEWER"
];
var RoleSchema = z.enum(ROLES);
var MERCHANT_ROLES = ROLES.filter((r) => r !== "SUPER_ADMIN");
var MerchantRoleSchema = z.enum([
  "MERCHANT_OWNER",
  "MERCHANT_ADMIN",
  "MERCHANT_SUPPORT",
  "VIEWER"
]);
var TenantStatusSchema = z.enum(["ACTIVE", "SUSPENDED", "DISABLED", "ARCHIVED"]);
var UserStatusSchema = z.enum(["ACTIVE", "DISABLED"]);
var ProviderKindSchema = z.enum(["XTREAM_COMPATIBLE"]);
var HostRoleSchema = z.enum(["PRIMARY", "BACKUP"]);
var HealthStatusSchema = z.enum(["UNKNOWN", "HEALTHY", "DEGRADED", "DOWN"]);
var MediaModeSchema = z.enum(["DIRECT", "GATEWAY"]);
var PLATFORMS = [
  "android_mobile",
  "android_tv",
  "fire_tv",
  "ios",
  "ipados",
  "tvos",
  "samsung_tv",
  "lg_tv"
];
var PlatformSchema = z.enum(PLATFORMS);
var ReleaseChannelSchema = z.enum(["play", "appstore", "amazon", "direct_apk"]);
var UpdatePolicySchema = z.enum(["NONE", "OPTIONAL", "REQUIRED", "BLOCKED"]);
var DeviceStatusSchema = z.enum(["ACTIVE", "BLOCKED"]);
var SessionStatusSchema = z.enum(["ACTIVE", "REVOKED", "EXPIRED"]);
var ContentTypeSchema = z.enum(["live", "movie", "series", "episode"]);
var ButtonStyleSchema = z.enum(["ROUNDED", "PILL", "SQUARE"]);
var SubscriptionStatusSchema = z.enum([
  "TRIALING",
  "ACTIVE",
  "PAST_DUE",
  "CANCELED",
  "EXPIRED"
]);
var FEATURE_KEYS = [
  "enableLive",
  "enableMovies",
  "enableSeries",
  "enableEPG",
  "enableFavorites",
  "enableContinueWatching",
  "enableSearch",
  "enableSupport",
  "enableExternalPlayer",
  "enableParentalControls",
  "enableMultiProfile"
];
var FeatureKeySchema = z.enum(FEATURE_KEYS);
var LanguageSchema = z.enum(["ar", "en"]);
var DEFAULT_FEATURES = {
  enableLive: true,
  enableMovies: true,
  enableSeries: true,
  enableEPG: true,
  enableFavorites: true,
  enableContinueWatching: true,
  enableSearch: true,
  enableSupport: true,
  enableExternalPlayer: false,
  enableParentalControls: false,
  enableMultiProfile: false
};

// src/errors.ts
import { z as z2 } from "zod";
var ERROR_CODES = [
  "VALIDATION_FAILED",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "NOT_FOUND",
  "CONFLICT",
  "RATE_LIMITED",
  "INVALID_CREDENTIALS",
  "INVALID_CODE",
  "ACCOUNT_LOCKED",
  "ACCOUNT_EXPIRED",
  "ACCOUNT_DISABLED",
  "DEVICE_LIMIT_REACHED",
  "DEVICE_BLOCKED",
  "SESSION_REVOKED",
  "TENANT_SUSPENDED",
  "TENANT_EXPIRED",
  "MAINTENANCE",
  "PROVIDER_UNAVAILABLE",
  "PROVIDER_NOT_CONFIGURED",
  "UNSUPPORTED_CONTENT",
  "UPDATE_REQUIRED",
  "PLAN_LIMIT_REACHED",
  "FEATURE_DISABLED",
  "INTERNAL"
];
var ErrorCodeSchema = z2.enum(ERROR_CODES);
var ApiErrorSchema = z2.object({
  error: z2.object({
    code: ErrorCodeSchema,
    message: z2.string(),
    retryable: z2.boolean(),
    requestId: z2.string().optional(),
    details: z2.array(z2.object({ path: z2.string(), message: z2.string() })).optional()
  })
});

// src/common.ts
import { z as z3 } from "zod";
var UuidSchema = z3.uuid();
var IdParamSchema = z3.object({ id: z3.uuid() });
var PageQuerySchema = z3.object({
  page: z3.coerce.number().int().min(1).default(1),
  pageSize: z3.coerce.number().int().min(1).max(200).default(50)
});
var pageOf = (item) => z3.object({
  items: z3.array(item),
  page: z3.number().int(),
  pageSize: z3.number().int(),
  total: z3.number().int()
});
var LocalizedTextSchema = z3.object({
  ar: z3.string().max(2e3).optional(),
  en: z3.string().max(2e3).optional()
});
var HexColorSchema = z3.string().regex(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/, "Expected #RRGGBB or #RRGGBBAA");
var HttpUrlSchema = z3.url({ protocol: /^https?$/ }).max(2048);
var SemverSchema = z3.string().regex(/^\d+\.\d+\.\d+$/, "Expected MAJOR.MINOR.PATCH");
var OkSchema = z3.object({ ok: z3.literal(true) });

// src/auth.ts
import { z as z4 } from "zod";
var PasswordSchema = z4.string().min(12, "At least 12 characters").max(256).refine((v) => /[a-z]/.test(v) && /[A-Z]/.test(v) && /\d/.test(v), {
  message: "Must include upper case, lower case and a digit"
});
var EmailSchema = z4.email().max(254).transform((v) => v.trim().toLowerCase());
var LoginRequestSchema = z4.object({
  email: EmailSchema,
  password: z4.string().min(1).max(256)
});
var PrincipalSchema = z4.object({
  userId: z4.uuid(),
  email: z4.string(),
  displayName: z4.string(),
  role: RoleSchema,
  tenantId: z4.uuid().nullable(),
  permissions: z4.array(z4.string()),
  twoFactorEnabled: z4.boolean(),
  /** Super admins always; merchant users when the admin requires it for their store. */
  twoFactorRequired: z4.boolean()
});
var AuthTokensResponseSchema = z4.object({
  accessToken: z4.string(),
  accessTokenExpiresAt: z4.iso.datetime(),
  csrfToken: z4.string(),
  user: PrincipalSchema
});
var ChangePasswordRequestSchema = z4.object({
  currentPassword: z4.string().min(1).max(256),
  newPassword: PasswordSchema
});
var TwoFactorCodeSchema = z4.string().max(32).transform((v) => v.replace(/[\s-]/g, "").toLowerCase()).pipe(z4.string().regex(/^(\d{6}|[a-z2-7]{8})$/, "Enter the 6-digit code or a recovery code"));
var TwoFactorChallengeSchema = z4.object({
  twoFactor: z4.enum(["VERIFY", "SETUP"]),
  challengeToken: z4.string()
});
var LoginResponseSchema = z4.union([AuthTokensResponseSchema, TwoFactorChallengeSchema]);
var ChallengeRequestSchema = z4.object({ challengeToken: z4.string().min(1).max(128) });
var VerifyChallengeRequestSchema = ChallengeRequestSchema.extend({
  code: TwoFactorCodeSchema
});
var TwoFactorSetupSchema = z4.object({
  secret: z4.string(),
  otpauthUri: z4.string()
});
var RecoveryCodesSchema = z4.object({ recoveryCodes: z4.array(z4.string()) });
var VerifiedLoginResponseSchema = AuthTokensResponseSchema.extend({
  recoveryCodes: z4.array(z4.string()).optional()
});
var TwoFactorCodeRequestSchema = z4.object({ code: TwoFactorCodeSchema });
var DisableTwoFactorRequestSchema = z4.object({
  password: z4.string().min(1).max(256),
  code: TwoFactorCodeSchema
});

// src/tenants.ts
import { z as z5 } from "zod";
var MerchantCodeSchema = z5.string().trim().transform((v) => v.toUpperCase()).pipe(z5.string().regex(/^[A-Z0-9]{4,16}$/, "Use 4-16 letters or digits"));
var PlanLimitsSchema = z5.object({
  maxDevices: z5.number().int().min(0),
  maxMerchantUsers: z5.number().int().min(1),
  maxHosts: z5.number().int().min(1),
  apiRateLimitPerMinute: z5.number().int().min(10)
});
var PlanCapabilitiesSchema = z5.object({
  fullBranding: z5.boolean(),
  analytics: z5.boolean(),
  gatewayMode: z5.boolean(),
  features: z5.partialRecord(FeatureKeySchema, z5.boolean())
});
var PlanSchema = z5.object({
  id: z5.uuid(),
  code: z5.string(),
  name: z5.string(),
  limits: PlanLimitsSchema,
  capabilities: PlanCapabilitiesSchema,
  isActive: z5.boolean()
});
var UpsertPlanRequestSchema = z5.object({
  code: z5.string().regex(/^[a-z0-9_-]{2,32}$/),
  name: z5.string().min(1).max(80),
  limits: PlanLimitsSchema,
  capabilities: PlanCapabilitiesSchema,
  isActive: z5.boolean().default(true)
});
var TenantSummarySchema = z5.object({
  id: z5.uuid(),
  name: z5.string(),
  status: TenantStatusSchema,
  planId: z5.uuid(),
  planCode: z5.string(),
  merchantCode: z5.string().nullable(),
  expiresAt: z5.iso.datetime().nullable(),
  maxDevicesOverride: z5.number().int().nullable(),
  maxUsersOverride: z5.number().int().nullable(),
  /** Test or demo store: left out of the platform's usage totals. */
  isTest: z5.boolean(),
  createdAt: z5.iso.datetime()
});
var TenantDetailSchema = TenantSummarySchema.extend({
  notes: z5.string().nullable(),
  featureOverrides: z5.partialRecord(FeatureKeySchema, z5.boolean()),
  /** Every panel user of the store must sign in with two-step verification. */
  requireTwoFactor: z5.boolean()
});
var CreateTenantRequestSchema = z5.object({
  name: z5.string().trim().min(2).max(120),
  planId: z5.uuid(),
  merchantCode: MerchantCodeSchema.optional(),
  expiresAt: z5.iso.datetime().nullable().optional(),
  maxDevicesOverride: z5.number().int().min(0).nullable().optional(),
  maxUsersOverride: z5.number().int().min(1).nullable().optional(),
  owner: z5.object({
    email: EmailSchema,
    displayName: z5.string().trim().min(1).max(120),
    password: PasswordSchema
  })
});
var UpdateTenantRequestSchema = z5.object({
  name: z5.string().trim().min(2).max(120).optional(),
  planId: z5.uuid().optional(),
  expiresAt: z5.iso.datetime().nullable().optional(),
  maxDevicesOverride: z5.number().int().min(0).nullable().optional(),
  maxUsersOverride: z5.number().int().min(1).nullable().optional(),
  notes: z5.string().max(2e3).nullable().optional(),
  requireTwoFactor: z5.boolean().optional(),
  isTest: z5.boolean().optional()
});
var SetTenantStatusRequestSchema = z5.object({
  status: z5.enum(["ACTIVE", "SUSPENDED", "DISABLED"]),
  reason: z5.string().max(500).optional()
});
var ResetMerchantCodeRequestSchema = z5.object({
  code: MerchantCodeSchema.optional()
});
var MerchantCodeResponseSchema = z5.object({ code: z5.string() });
var TenantUserSchema = z5.object({
  id: z5.uuid(),
  email: z5.string(),
  displayName: z5.string(),
  role: RoleSchema,
  status: UserStatusSchema,
  lastLoginAt: z5.iso.datetime().nullable(),
  twoFactorEnabled: z5.boolean(),
  createdAt: z5.iso.datetime()
});
var CreateTenantUserRequestSchema = z5.object({
  email: EmailSchema,
  displayName: z5.string().trim().min(1).max(120),
  role: MerchantRoleSchema,
  password: PasswordSchema
});
var UpdateTenantUserRequestSchema = z5.object({
  displayName: z5.string().trim().min(1).max(120).optional(),
  role: MerchantRoleSchema.optional(),
  status: UserStatusSchema.optional()
});

// src/branding.ts
import { z as z6 } from "zod";
var BrandColorsSchema = z6.object({
  primary: HexColorSchema,
  secondary: HexColorSchema,
  accent: HexColorSchema,
  background: HexColorSchema,
  surface: HexColorSchema,
  onPrimary: HexColorSchema,
  text: HexColorSchema
});
var SupportInfoSchema = z6.object({
  phone: z6.string().regex(/^\+?[0-9 ()-]{5,24}$/).nullable(),
  whatsapp: z6.string().regex(/^\+?[0-9]{5,20}$/).nullable(),
  telegram: z6.string().regex(/^@?[A-Za-z0-9_]{3,64}$/).nullable(),
  email: z6.email().nullable(),
  website: HttpUrlSchema.nullable(),
  termsUrl: HttpUrlSchema.nullable(),
  privacyUrl: HttpUrlSchema.nullable()
});
var BrandMessagesSchema = z6.object({
  welcome: LocalizedTextSchema.optional(),
  loginHint: LocalizedTextSchema.optional(),
  emptyState: LocalizedTextSchema.optional(),
  maintenance: LocalizedTextSchema.optional()
});
var BRAND_ASSET_KINDS = ["logo", "splash", "background", "loginBackground"];
var BrandAssetKindSchema = z6.enum(BRAND_ASSET_KINDS);
var BrandingSchema = z6.object({
  storeName: z6.string().trim().min(1).max(80),
  appName: z6.string().trim().min(1).max(40),
  colors: BrandColorsSchema,
  buttonStyle: ButtonStyleSchema,
  support: SupportInfoSchema,
  messages: BrandMessagesSchema,
  assets: z6.object({
    logoUrl: z6.string().nullable(),
    splashUrl: z6.string().nullable(),
    backgroundUrl: z6.string().nullable(),
    loginBackgroundUrl: z6.string().nullable()
  }),
  version: z6.number().int()
});
var UpdateBrandingRequestSchema = z6.object({
  storeName: z6.string().trim().min(1).max(80).optional(),
  appName: z6.string().trim().min(1).max(40).optional(),
  colors: BrandColorsSchema.optional(),
  buttonStyle: ButtonStyleSchema.optional(),
  support: SupportInfoSchema.partial().optional(),
  messages: BrandMessagesSchema.optional()
});
var ASSET_CONTENT_TYPES = ["image/png", "image/jpeg", "image/webp"];
var AssetUploadRequestSchema = z6.object({
  kind: BrandAssetKindSchema,
  contentType: z6.enum(ASSET_CONTENT_TYPES),
  sizeBytes: z6.number().int().min(1).max(5 * 1024 * 1024)
});
var AssetUploadResponseSchema = z6.object({
  uploadUrl: z6.string(),
  headers: z6.record(z6.string(), z6.string()),
  assetKey: z6.string(),
  expiresAt: z6.iso.datetime()
});
var AssetCommitRequestSchema = z6.object({
  kind: BrandAssetKindSchema,
  assetKey: z6.string().min(1).max(512)
});
var MinVersionPolicySchema = z6.partialRecord(
  PlatformSchema,
  z6.object({ minimumVersion: SemverSchema, updateType: z6.enum(["OPTIONAL", "REQUIRED"]) })
);
var ParentalSettingsSchema = z6.object({
  enabled: z6.boolean(),
  /** Category ids locked behind the PIN, per content type. */
  lockedCategoryIds: z6.object({
    live: z6.array(z6.string().max(64)).max(500),
    movie: z6.array(z6.string().max(64)).max(500),
    series: z6.array(z6.string().max(64)).max(500)
  }),
  /** Hide categories the provider flags as adult, in addition to explicit locks. */
  hideAdultCategories: z6.boolean()
});
var EXPIRY_DAYS_PLACEHOLDER = "{days}";
var EXPIRY_REMINDER_MAX_DAYS = 60;
var ExpiryReminderSchema = z6.object({
  enabled: z6.boolean(),
  daysBefore: z6.number().int().min(1).max(EXPIRY_REMINDER_MAX_DAYS),
  message: z6.object({
    ar: z6.string().trim().max(300).optional(),
    en: z6.string().trim().max(300).optional()
  })
});
var TenantSettingsSchema = z6.object({
  mediaMode: MediaModeSchema,
  defaultLanguage: LanguageSchema,
  maintenance: z6.object({ enabled: z6.boolean(), message: LocalizedTextSchema }),
  features: z6.partialRecord(FeatureKeySchema, z6.boolean()),
  autoPlayNextEpisode: z6.boolean(),
  minVersions: MinVersionPolicySchema,
  parental: ParentalSettingsSchema,
  expiryReminder: ExpiryReminderSchema
});
var UpdateTenantSettingsRequestSchema = TenantSettingsSchema.partial();

// src/hosts.ts
import { z as z7 } from "zod";
var LocalCheckVerdictSchema = z7.enum([
  "WORKS",
  "BLOCKED",
  "DOWN",
  "NO_PLAYBACK",
  "UNCONFIRMED"
]);
var LocalCheckSchema = z7.object({
  verdict: LocalCheckVerdictSchema,
  latencyMs: z7.number().int().nullable(),
  checkedAt: z7.iso.datetime()
});
var HostSchema = z7.object({
  id: z7.uuid(),
  label: z7.string(),
  providerKind: ProviderKindSchema,
  /** The provider this host is a mirror of; null when the store has not grouped it. */
  providerId: z7.uuid().nullable(),
  /** Masked origin, e.g. "http://pa•••.example.com:8080", for staff who only view hosts. */
  maskedUrl: z7.string(),
  /** The full address, for staff who may edit hosts; null for read-only staff. */
  url: z7.string().nullable(),
  role: HostRoleSchema,
  priority: z7.number().int(),
  isEnabled: z7.boolean(),
  healthStatus: HealthStatusSchema,
  lastCheckedAt: z7.iso.datetime().nullable(),
  lastLatencyMs: z7.number().int().nullable(),
  /** When the current run of failed checks with the host's check login began. */
  failingSince: z7.iso.datetime().nullable(),
  /** Set while the health job keeps the host out of rotation; cleared when it answers again. */
  autoDisabledAt: z7.iso.datetime().nullable(),
  /** The last result from the store's check link, run on a device in the subscribers' country. */
  localCheck: LocalCheckSchema.nullable(),
  createdAt: z7.iso.datetime()
});
var CreateHostRequestSchema = z7.object({
  label: z7.string().trim().min(1).max(80),
  providerKind: ProviderKindSchema.default("XTREAM_COMPATIBLE"),
  url: HttpUrlSchema,
  providerId: z7.uuid().nullable().optional(),
  role: HostRoleSchema,
  priority: z7.number().int().min(0).max(1e3).default(100),
  isEnabled: z7.boolean().default(true)
});
var UpdateHostRequestSchema = z7.object({
  label: z7.string().trim().min(1).max(80).optional(),
  url: HttpUrlSchema.optional(),
  providerId: z7.uuid().nullable().optional(),
  role: HostRoleSchema.optional(),
  priority: z7.number().int().min(0).max(1e3).optional(),
  isEnabled: z7.boolean().optional()
});
var TestAccountSchema = z7.object({
  username: z7.string().trim().min(1).max(128),
  password: z7.string().min(1).max(256)
});
var HostTestRequestSchema = z7.object({ account: TestAccountSchema.optional() }).default({});
var HostTestResultSchema = z7.object({
  ok: z7.boolean(),
  latencyMs: z7.number().int().nullable(),
  status: HealthStatusSchema,
  errorCode: z7.string().nullable()
});
var HostHealthCheckSchema = z7.object({
  checkedAt: z7.iso.datetime(),
  ok: z7.boolean(),
  latencyMs: z7.number().int().nullable(),
  errorCode: z7.string().nullable()
});
var MAX_BULK_HOSTS = 50;
var BulkUrlsSchema = z7.array(z7.string().trim().min(1).max(2048)).min(1).max(MAX_BULK_HOSTS);
var HostProbeRequestSchema = z7.object({
  urls: BulkUrlsSchema,
  account: TestAccountSchema.optional(),
  /** Without an account, the links are tested with this provider's saved check login. */
  providerId: z7.uuid().nullable().optional()
});
var HostProbeResultSchema = z7.object({
  ok: z7.boolean(),
  latencyMs: z7.number().int().nullable(),
  errorCode: z7.string().nullable(),
  duplicate: z7.enum(["EXISTING", "REPEATED"]).nullable()
});
var BulkCreateHostsRequestSchema = z7.object({
  label: z7.string().trim().min(1).max(70),
  urls: BulkUrlsSchema,
  providerId: z7.uuid().nullable().optional()
});
var HostTestAllResultSchema = HostTestResultSchema.extend({ hostId: z7.uuid() });
var HostUsageSchema = z7.object({
  hostId: z7.uuid(),
  /** Using the app in the last few minutes on a device signed in through this host. */
  online: z7.number().int(),
  /** Used the app today (report time zone) on a device signed in through this host. */
  today: z7.number().int(),
  /** Signed in through this host, whether or not they are watching now. */
  signedIn: z7.number().int()
});
var BulkDeleteHostsRequestSchema = z7.object({
  ids: z7.array(z7.uuid()).min(1).max(MAX_BULK_HOSTS)
});
var HostAutoActionSchema = z7.object({
  action: z7.enum(["disabled", "enabled", "deleted"]),
  label: z7.string(),
  at: z7.iso.datetime()
});
var CheckLoginFields = {
  configured: z7.boolean(),
  /** Masked, e.g. "ab•••yz". */
  username: z7.string().nullable(),
  /** Every host it is used on refused it on the last round: the account expired or is wrong. */
  rejected: z7.boolean()
};
var HostProviderSchema = z7.object({
  id: z7.uuid(),
  name: z7.string(),
  hostCount: z7.number().int(),
  ...CheckLoginFields
});
var MAX_HOST_PROVIDERS = 20;
var HostProviderRequestSchema = z7.object({ name: z7.string().trim().min(1).max(40) });
var ProviderHostsRequestSchema = z7.object({
  hostIds: z7.array(z7.uuid()).max(500)
});
var HostMonitorSchema = z7.object({
  ...CheckLoginFields,
  providers: z7.array(HostProviderSchema),
  checkedAt: z7.iso.datetime().nullable(),
  /** Hosts taken out of rotation, put back or deleted in the last 7 days, newest first. */
  recent: z7.array(HostAutoActionSchema)
});
var SetHostMonitorRequestSchema = TestAccountSchema;
var LibraryHostSchema = z7.object({
  /** Stands for the address in requests; the same in every store. */
  fingerprint: z7.string(),
  url: z7.string(),
  /** The best status any store saw. */
  healthStatus: HealthStatusSchema,
  lastCheckedAt: z7.iso.datetime().nullable(),
  /** Stores that have this host. */
  storeCount: z7.number().int()
});
var LibraryLinkSchema = z7.object({
  id: z7.uuid(),
  tenantId: z7.uuid(),
  tenantName: z7.string(),
  /** Hosts the store now has under this provider. */
  hostCount: z7.number().int(),
  createdAt: z7.iso.datetime()
});
var LibraryProviderSchema = z7.object({
  /** The name in lower case; identifies the provider across stores. */
  key: z7.string(),
  name: z7.string(),
  hosts: z7.array(LibraryHostSchema),
  links: z7.array(LibraryLinkSchema)
});
var SendLibraryHostsRequestSchema = z7.object({
  tenantId: z7.uuid(),
  providerKey: z7.string().trim().min(1).max(40),
  fingerprints: z7.array(z7.string().min(1).max(128)).min(1).max(500).optional()
});
var SendLibraryHostsResultSchema = z7.object({
  added: z7.number().int(),
  /** Already in the store. */
  existing: z7.number().int(),
  /** Left out because the store's plan has no room for more hosts. */
  overLimit: z7.number().int(),
  linked: z7.boolean()
});
var HostCheckLinkSchema = z7.object({
  available: z7.boolean(),
  url: z7.string().nullable(),
  createdAt: z7.iso.datetime().nullable()
});
var HostCheckPlanSchema = z7.object({
  store: z7.string(),
  groups: z7.array(
    z7.object({
      /** The provider; null for the store's hosts without one. */
      providerId: z7.uuid().nullable(),
      name: z7.string().nullable(),
      login: TestAccountSchema.nullable(),
      hosts: z7.array(z7.object({ id: z7.uuid(), label: z7.string(), url: z7.string() }))
    })
  )
});
var HostCheckReferenceRequestSchema = z7.object({
  hostId: z7.uuid(),
  /** A login typed on the device for a provider without a saved one; used once, never stored. */
  account: TestAccountSchema.optional(),
  /** Also find a live channel, for devices that can play one as a last proof. */
  wantStream: z7.boolean().default(false)
});
var HostCheckReferenceSchema = z7.object({
  /** The host answered our server. */
  reachable: z7.boolean(),
  /** The host lets web pages read its answers, so a browser that cannot read one got another's. */
  browserReadable: z7.boolean(),
  /** A live channel of the login, when asked for and the login works. */
  liveStreamId: z7.string().nullable()
});
var HostCheckResultsRequestSchema = z7.object({
  results: z7.array(
    z7.object({
      hostId: z7.uuid(),
      verdict: LocalCheckVerdictSchema,
      latencyMs: z7.number().int().min(0).max(6e5).nullable()
    })
  ).min(1).max(500)
});
var HostUploadLinkSchema = z7.object({
  available: z7.boolean(),
  url: z7.string().nullable(),
  createdAt: z7.iso.datetime().nullable(),
  /** The store the hosts are added to. */
  tenantId: z7.uuid().nullable(),
  tenantName: z7.string().nullable()
});
var CreateHostUploadLinkRequestSchema = z7.object({ tenantId: z7.uuid() });
var HostUploadPlanSchema = z7.object({
  store: z7.string(),
  providers: z7.array(
    z7.object({ id: z7.uuid(), name: z7.string(), login: TestAccountSchema.nullable() })
  )
});
var HostUploadReferenceRequestSchema = z7.object({
  url: z7.string().trim().min(1).max(2048),
  /** The provider the host mirrors, whose channels the device may play. */
  providerName: z7.string().trim().min(1).max(40),
  account: TestAccountSchema,
  wantStream: z7.boolean().default(false)
});
var HostUploadAddRequestSchema = z7.object({
  providerName: z7.string().trim().min(1).max(40),
  account: TestAccountSchema,
  urls: BulkUrlsSchema
});
var HostUploadAddResultSchema = z7.object({
  added: z7.number().int(),
  /** Already in the store. */
  existing: z7.number().int(),
  /** Left out because the store's plan has no room for more hosts. */
  overLimit: z7.number().int()
});

// src/client.ts
import { z as z8 } from "zod";
var VersionPolicyResultSchema = z8.object({
  policy: UpdatePolicySchema,
  latestVersion: z8.string().nullable(),
  minimumVersion: z8.string().nullable(),
  releaseNotes: LocalizedTextSchema.nullable(),
  downloadUrl: z8.string().nullable(),
  storeUrl: z8.string().nullable()
});
var TenantBundleSchema = z8.object({
  tenantId: z8.uuid(),
  code: z8.string(),
  branding: BrandingSchema,
  features: z8.record(FeatureKeySchema, z8.boolean()),
  defaultLanguage: LanguageSchema,
  auth: z8.object({ fields: z8.array(z8.enum(["username", "password"])) }),
  maintenance: z8.object({ enabled: z8.boolean(), message: LocalizedTextSchema }),
  announcements: z8.array(
    z8.object({
      id: z8.uuid(),
      title: LocalizedTextSchema,
      body: LocalizedTextSchema,
      severity: z8.enum(["info", "warning", "critical"])
    })
  ),
  playback: z8.object({ autoPlayNextEpisode: z8.boolean() }),
  parental: z8.object({ enabled: z8.boolean() }),
  /** Null when the store has the reminder off. */
  expiryReminder: ExpiryReminderSchema.omit({ enabled: true }).nullable(),
  etag: z8.string()
});
var ClientInfoSchema = z8.object({
  installId: z8.uuid(),
  platform: PlatformSchema,
  channel: ReleaseChannelSchema.optional(),
  appVersion: SemverSchema,
  osVersion: z8.string().trim().max(40),
  model: z8.string().trim().max(80),
  deviceName: z8.string().trim().max(80).optional()
});
var ResolveQuerySchema = z8.object({
  platform: PlatformSchema.optional(),
  channel: ReleaseChannelSchema.optional(),
  appVersion: SemverSchema.optional()
});
var ResolveResponseSchema = z8.object({
  bundle: TenantBundleSchema,
  version: VersionPolicyResultSchema
});
var ClientLoginRequestSchema = z8.object({
  tenantId: z8.uuid(),
  username: z8.string().trim().min(1).max(128),
  password: z8.string().min(1).max(256),
  client: ClientInfoSchema
});
var ProviderAccountSchema = z8.object({
  status: z8.enum(["ACTIVE", "EXPIRED", "DISABLED", "TRIAL"]),
  expiresAt: z8.iso.datetime().nullable(),
  maxConnections: z8.number().int().nullable(),
  activeConnections: z8.number().int().nullable(),
  isTrial: z8.boolean(),
  createdAt: z8.iso.datetime().nullable()
});
var ClientSessionResponseSchema = z8.object({
  accessToken: z8.string(),
  accessTokenExpiresAt: z8.iso.datetime(),
  refreshToken: z8.string(),
  refreshTokenExpiresAt: z8.iso.datetime(),
  sessionId: z8.uuid(),
  account: ProviderAccountSchema,
  version: VersionPolicyResultSchema
});
var ClientRefreshRequestSchema = z8.object({
  refreshToken: z8.string().min(20).max(256),
  client: ClientInfoSchema.pick({ installId: true, appVersion: true })
});
var CategorySchema = z8.object({
  id: z8.string(),
  name: z8.string(),
  parentId: z8.string().nullable(),
  isAdult: z8.boolean(),
  locked: z8.boolean()
});
var ChannelSchema = z8.object({
  id: z8.string(),
  number: z8.number().int().nullable(),
  name: z8.string(),
  logoUrl: z8.string().nullable(),
  categoryId: z8.string().nullable(),
  epgChannelId: z8.string().nullable(),
  hasArchive: z8.boolean(),
  isAdult: z8.boolean()
});
var MovieSchema = z8.object({
  id: z8.string(),
  name: z8.string(),
  posterUrl: z8.string().nullable(),
  categoryId: z8.string().nullable(),
  rating: z8.number().nullable(),
  year: z8.number().int().nullable(),
  addedAt: z8.iso.datetime().nullable(),
  containerExtension: z8.string().nullable(),
  isAdult: z8.boolean()
});
var MovieDetailsSchema = MovieSchema.extend({
  description: z8.string().nullable(),
  durationSeconds: z8.number().int().nullable(),
  releaseDate: z8.string().nullable(),
  genres: z8.array(z8.string()),
  cast: z8.array(z8.string()),
  director: z8.string().nullable(),
  backdropUrl: z8.string().nullable(),
  trailerUrl: z8.string().nullable()
});
var SeriesSchema = z8.object({
  id: z8.string(),
  name: z8.string(),
  posterUrl: z8.string().nullable(),
  categoryId: z8.string().nullable(),
  rating: z8.number().nullable(),
  year: z8.number().int().nullable(),
  updatedAt: z8.iso.datetime().nullable(),
  isAdult: z8.boolean()
});
var EpisodeSchema = z8.object({
  id: z8.string(),
  seriesId: z8.string(),
  seasonNumber: z8.number().int(),
  episodeNumber: z8.number().int(),
  title: z8.string(),
  description: z8.string().nullable(),
  durationSeconds: z8.number().int().nullable(),
  thumbnailUrl: z8.string().nullable(),
  containerExtension: z8.string().nullable()
});
var SeasonSchema = z8.object({
  seasonNumber: z8.number().int(),
  name: z8.string(),
  posterUrl: z8.string().nullable(),
  episodes: z8.array(EpisodeSchema)
});
var SeriesDetailsSchema = SeriesSchema.extend({
  description: z8.string().nullable(),
  genres: z8.array(z8.string()),
  cast: z8.array(z8.string()),
  backdropUrl: z8.string().nullable(),
  seasons: z8.array(SeasonSchema)
});
var EpgProgramSchema = z8.object({
  id: z8.string(),
  channelId: z8.string(),
  title: z8.string(),
  description: z8.string().nullable(),
  start: z8.iso.datetime(),
  end: z8.iso.datetime()
});
var CatalogListQuerySchema = z8.object({
  categoryId: z8.string().max(64).optional(),
  search: z8.string().trim().max(100).optional(),
  page: z8.coerce.number().int().min(1).default(1),
  pageSize: z8.coerce.number().int().min(1).max(200).default(60)
});
var EpgQuerySchema = z8.object({
  channelId: z8.string().max(64),
  from: z8.iso.datetime().optional(),
  to: z8.iso.datetime().optional()
});
var SearchQuerySchema = z8.object({
  q: z8.string().trim().min(2).max(100),
  limit: z8.coerce.number().int().min(1).max(50).default(20)
});
var SearchResultSchema = z8.object({
  channels: z8.array(ChannelSchema),
  movies: z8.array(MovieSchema),
  series: z8.array(SeriesSchema)
});
var PlaybackTicketRequestSchema = z8.object({
  contentType: ContentTypeSchema.exclude(["series"]),
  contentId: z8.string().min(1).max(64),
  /** Container extension for VOD/episodes as reported by the catalog. */
  containerExtension: z8.string().regex(/^[a-z0-9]{2,5}$/).optional(),
  /**
   * Live output the subscriber picked in the app (Xtream `.ts` or `.m3u8`). The account's allowed
   * formats still win; absent means the server picks for the device.
   */
  liveFormat: z8.enum(["ts", "m3u8"]).optional(),
  /**
   * The stream from the subscriber's current host would not open (a host can be blocked for
   * streams while its API still answers): move this session to the next host that accepts the
   * account and issue the ticket from there.
   */
  switchHost: z8.boolean().optional()
});
var StreamFormatSchema = z8.enum(["hls", "dash", "mpegts", "progressive"]);
var PlaybackTicketSchema = z8.object({
  url: z8.string(),
  format: StreamFormatSchema,
  expiresAt: z8.iso.datetime(),
  isLive: z8.boolean(),
  resumePositionSeconds: z8.number().int().nullable()
});
var LibraryItemMetaSchema = z8.object({
  title: z8.string().max(300),
  imageUrl: z8.string().max(2048).nullable(),
  seriesId: z8.string().max(64).nullable().optional(),
  seasonNumber: z8.number().int().nullable().optional(),
  episodeNumber: z8.number().int().nullable().optional(),
  containerExtension: z8.string().max(5).nullable().optional()
});
var FavoriteSchema = z8.object({
  contentType: ContentTypeSchema,
  contentId: z8.string(),
  meta: LibraryItemMetaSchema,
  createdAt: z8.iso.datetime()
});
var AddFavoriteRequestSchema = z8.object({
  contentType: ContentTypeSchema,
  contentId: z8.string().min(1).max(64),
  meta: LibraryItemMetaSchema
});
var WatchProgressRequestSchema = z8.object({
  contentType: ContentTypeSchema.exclude(["series"]),
  contentId: z8.string().min(1).max(64),
  positionSeconds: z8.number().int().min(0),
  durationSeconds: z8.number().int().min(0).nullable(),
  meta: LibraryItemMetaSchema
});
var HistoryItemSchema = z8.object({
  contentType: ContentTypeSchema,
  contentId: z8.string(),
  positionSeconds: z8.number().int(),
  durationSeconds: z8.number().int().nullable(),
  completed: z8.boolean(),
  meta: LibraryItemMetaSchema,
  updatedAt: z8.iso.datetime()
});

// src/devices.ts
import { z as z9 } from "zod";
var DeviceSchema = z9.object({
  id: z9.uuid(),
  customerUsername: z9.string(),
  deviceName: z9.string().nullable(),
  model: z9.string().nullable(),
  platform: PlatformSchema,
  osVersion: z9.string().nullable(),
  appVersion: z9.string().nullable(),
  status: DeviceStatusSchema,
  lastLoginAt: z9.iso.datetime().nullable(),
  lastActivityAt: z9.iso.datetime().nullable(),
  activeSessions: z9.number().int(),
  createdAt: z9.iso.datetime()
});
var DeviceListQuerySchema = z9.object({
  search: z9.string().trim().max(128).optional(),
  status: DeviceStatusSchema.optional(),
  platform: PlatformSchema.optional(),
  page: z9.coerce.number().int().min(1).default(1),
  pageSize: z9.coerce.number().int().min(1).max(200).default(50)
});
var CustomerSessionSchema = z9.object({
  id: z9.uuid(),
  deviceId: z9.uuid(),
  status: SessionStatusSchema,
  createdAt: z9.iso.datetime(),
  lastSeenAt: z9.iso.datetime().nullable()
});

// src/releases.ts
import { z as z10 } from "zod";
var AppVersionSchema = z10.object({
  id: z10.uuid(),
  platform: PlatformSchema,
  channel: ReleaseChannelSchema,
  latestVersion: z10.string(),
  minimumVersion: z10.string(),
  blockedVersions: z10.array(z10.string()),
  releaseNotes: LocalizedTextSchema,
  downloadUrl: z10.string().nullable(),
  storeUrl: z10.string().nullable(),
  sha256: z10.string().nullable(),
  fileSizeBytes: z10.number().int().nullable(),
  publishedAt: z10.iso.datetime()
});
var UpsertAppVersionRequestSchema = z10.object({
  platform: PlatformSchema,
  channel: ReleaseChannelSchema,
  latestVersion: SemverSchema,
  minimumVersion: SemverSchema,
  blockedVersions: z10.array(SemverSchema).max(100).default([]),
  releaseNotes: LocalizedTextSchema.default({}),
  downloadUrl: HttpUrlSchema.nullable().default(null),
  storeUrl: HttpUrlSchema.nullable().default(null),
  sha256: z10.string().regex(/^[a-f0-9]{64}$/).nullable().default(null),
  fileSizeBytes: z10.number().int().min(1).nullable().default(null)
});
var PublicLatestReleaseSchema = z10.object({
  platform: PlatformSchema,
  version: z10.string(),
  releaseNotes: LocalizedTextSchema,
  downloadUrl: z10.string().nullable(),
  sha256: z10.string().nullable(),
  fileSizeBytes: z10.number().int().nullable(),
  publishedAt: z10.iso.datetime()
});
var APK_PLATFORMS = [
  "android_mobile",
  "android_tv",
  "fire_tv"
];
var isApkPlatform = (p) => APK_PLATFORMS.includes(p);
var TV_WEB_PLATFORMS = ["samsung_tv", "lg_tv"];
var isTvWebPlatform = (p) => TV_WEB_PLATFORMS.includes(p);
var VERSIONED_PLATFORMS = PLATFORMS.filter((p) => !isTvWebPlatform(p));
var PublishReleaseRequestSchema = z10.object({
  platforms: z10.array(PlatformSchema).min(1).refine((v) => new Set(v).size === v.length, "Duplicate platform").refine((v) => !v.some(isTvWebPlatform), "Samsung and LG TVs update through TV builds"),
  version: SemverSchema,
  required: z10.boolean().default(false),
  releaseNotes: LocalizedTextSchema.default({}),
  downloadUrl: HttpUrlSchema.nullable().default(null),
  storeUrl: HttpUrlSchema.nullable().default(null),
  sha256: z10.string().regex(/^[a-f0-9]{64}$/).nullable().default(null),
  fileSizeBytes: z10.number().int().min(1).nullable().default(null)
}).superRefine((v, ctx) => {
  if (v.platforms.some(isApkPlatform) && !v.downloadUrl)
    ctx.addIssue({ code: "custom", path: ["downloadUrl"], message: "Required for Android" });
  if (v.platforms.some((p) => !isApkPlatform(p)) && !v.storeUrl)
    ctx.addIssue({ code: "custom", path: ["storeUrl"], message: "Required for Apple" });
});
var ReleaseSourceSchema = z10.object({
  version: z10.string(),
  downloadUrl: z10.string(),
  sha256: z10.string().nullable(),
  fileSizeBytes: z10.number().int().nullable(),
  publishedAt: z10.iso.datetime()
});
var PilotReleaseSchema = z10.object({
  version: z10.string(),
  downloadUrl: z10.string(),
  sha256: z10.string().nullable(),
  fileSizeBytes: z10.number().int().nullable(),
  builtAt: z10.iso.datetime(),
  foundAt: z10.iso.datetime(),
  testedOn: z10.number().int()
});
var PilotStateSchema = z10.object({ pilot: PilotReleaseSchema.nullable() });
var PromotePilotRequestSchema = z10.object({
  version: SemverSchema,
  required: z10.boolean().default(false),
  releaseNotes: LocalizedTextSchema.default({})
});
var DismissPilotRequestSchema = z10.object({ version: SemverSchema });
var ReleaseTesterSchema = z10.object({
  id: z10.uuid(),
  tenantId: z10.uuid(),
  storeName: z10.string(),
  customerUsername: z10.string(),
  /** Devices signed in to this account, with the version each runs. */
  devices: z10.array(z10.object({ platform: PlatformSchema, appVersion: z10.string().nullable() })),
  createdAt: z10.iso.datetime()
});
var AddReleaseTesterRequestSchema = z10.object({
  tenantId: z10.uuid(),
  customerUsername: z10.string().trim().min(1).max(128)
});
var TvBuildSchema = z10.object({
  version: z10.string(),
  status: z10.enum(["PILOT", "PUBLISHED", "DISMISSED"]),
  minShellVersion: z10.string(),
  sizeBytes: z10.number().int(),
  builtAt: z10.iso.datetime(),
  foundAt: z10.iso.datetime(),
  decidedAt: z10.iso.datetime().nullable()
});
var TvReleaseStateSchema = z10.object({
  current: TvBuildSchema.nullable(),
  pilot: TvBuildSchema.extend({ testedOn: z10.number().int() }).nullable()
});
var TvBuildDecisionRequestSchema = z10.object({ version: SemverSchema });
var TvAppRequestSchema = z10.object({
  platform: z10.enum(TV_WEB_PLATFORMS),
  shellVersion: SemverSchema,
  storeCode: z10.string().trim().regex(/^[A-Za-z0-9]{4,16}$/).optional(),
  username: z10.string().trim().min(1).max(128).optional()
});
var TvAppResponseSchema = z10.object({
  version: z10.string().nullable(),
  script: z10.string().nullable(),
  style: z10.string().nullable()
});

// src/platform.ts
import { z as z11 } from "zod";
var GlobalSettingsSchema = z11.object({
  maintenance: z11.object({ enabled: z11.boolean(), message: LocalizedTextSchema }),
  features: z11.record(FeatureKeySchema, z11.boolean())
});
var UpdateGlobalSettingsRequestSchema = z11.object({
  maintenance: z11.object({ enabled: z11.boolean(), message: LocalizedTextSchema }).optional(),
  features: z11.partialRecord(FeatureKeySchema, z11.boolean()).optional()
});
var TenantFeatureOverridesSchema = z11.object({
  overrides: z11.partialRecord(FeatureKeySchema, z11.boolean().nullable())
});
var AnnouncementSchema = z11.object({
  id: z11.uuid(),
  tenantId: z11.uuid().nullable(),
  title: LocalizedTextSchema,
  body: LocalizedTextSchema,
  severity: z11.enum(["info", "warning", "critical"]),
  audience: z11.enum(["CUSTOMERS", "MERCHANTS"]),
  startsAt: z11.iso.datetime(),
  endsAt: z11.iso.datetime().nullable(),
  createdAt: z11.iso.datetime()
});
var CreateAnnouncementRequestSchema = z11.object({
  tenantId: z11.uuid().nullable().default(null),
  title: LocalizedTextSchema,
  body: LocalizedTextSchema,
  severity: z11.enum(["info", "warning", "critical"]).default("info"),
  audience: z11.enum(["CUSTOMERS", "MERCHANTS"]).default("CUSTOMERS"),
  startsAt: z11.iso.datetime().optional(),
  endsAt: z11.iso.datetime().nullable().optional()
});
var AuditLogSchema = z11.object({
  id: z11.uuid(),
  tenantId: z11.uuid().nullable(),
  actorType: z11.enum(["USER", "CUSTOMER", "SYSTEM"]),
  actorId: z11.string().nullable(),
  action: z11.string(),
  targetType: z11.string().nullable(),
  targetId: z11.string().nullable(),
  ip: z11.string().nullable(),
  metadata: z11.record(z11.string(), z11.unknown()),
  createdAt: z11.iso.datetime()
});
var AuditQuerySchema = z11.object({
  tenantId: z11.uuid().optional(),
  action: z11.string().max(64).optional(),
  page: z11.coerce.number().int().min(1).default(1),
  pageSize: z11.coerce.number().int().min(1).max(200).default(50)
});
var AudienceSchema = z11.object({
  subscribers: z11.number().int(),
  devices: z11.number().int()
});
var ActivityReportSchema = z11.object({
  /** Seen in the last few minutes (the apps send a heartbeat while open). */
  online: AudienceSchema,
  today: AudienceSchema,
  last7Days: AudienceSchema,
  last30Days: AudienceSchema,
  /** Oldest first, one entry per day for the last 30 days, today included. */
  daily: z11.array(AudienceSchema.extend({ day: z11.iso.date() })),
  /** Devices used in the last 30 days, per platform. */
  platforms: z11.array(z11.object({ platform: z11.string(), devices: z11.number().int() })),
  /** App version each of those devices used last. */
  appVersions: z11.array(
    z11.object({ platform: z11.string(), appVersion: z11.string(), devices: z11.number().int() })
  ),
  timeZone: z11.string()
});
var LoginCountsSchema = z11.object({
  successes24h: z11.number().int(),
  failures24h: z11.number().int()
});
var HostCountsSchema = z11.object({
  total: z11.number().int(),
  healthy: z11.number().int(),
  down: z11.number().int(),
  unchecked: z11.number().int(),
  /** Turned off, by the merchant or by the health job. */
  disabled: z11.number().int()
});
var TenantUsageSchema = z11.object({
  tenantId: z11.uuid(),
  activity: ActivityReportSchema,
  devices: z11.object({
    /** Devices holding a session: what the plan's device limit counts. */
    inUse: z11.number().int(),
    limit: z11.number().int(),
    blocked: z11.number().int()
  }),
  logins: LoginCountsSchema,
  hosts: HostCountsSchema
});
var PlatformOverviewSchema = z11.object({
  /** Every store except archived and test stores. */
  activity: ActivityReportSchema,
  tenants: z11.object({
    total: z11.number().int(),
    active: z11.number().int(),
    suspended: z11.number().int(),
    test: z11.number().int()
  }),
  merchants: z11.array(
    z11.object({
      id: z11.uuid(),
      name: z11.string(),
      status: TenantStatusSchema,
      isTest: z11.boolean(),
      online: AudienceSchema,
      today: AudienceSchema,
      last30Days: AudienceSchema,
      hosts: HostCountsSchema
    })
  ),
  system: z11.object({
    database: z11.boolean(),
    redis: z11.boolean(),
    hostsDown: z11.number().int(),
    logins: LoginCountsSchema
  })
});
var GrowthCountsSchema = z11.object({
  newSubscribers: z11.number().int(),
  newDevices: z11.number().int(),
  downloads: z11.number().int(),
  updates: z11.number().int()
});
var GrowthReportSchema = z11.object({
  today: GrowthCountsSchema,
  last7Days: GrowthCountsSchema,
  last30Days: GrowthCountsSchema,
  allTime: GrowthCountsSchema,
  /** Oldest first, one entry per day for the last 30 days, today included. */
  daily: z11.array(GrowthCountsSchema.extend({ day: z11.iso.date() })),
  /** New subscribers each store brought, most in the last 30 days first. */
  merchants: z11.array(
    z11.object({
      id: z11.uuid(),
      name: z11.string(),
      status: TenantStatusSchema,
      today: z11.number().int(),
      last7Days: z11.number().int(),
      last30Days: z11.number().int(),
      allTime: z11.number().int()
    })
  ),
  /** First day downloads were counted (they cannot be known before); null until the first one. */
  downloadsSince: z11.iso.date().nullable(),
  timeZone: z11.string()
});

// src/permissions.ts
var PERMISSIONS = [
  "platform:manage",
  "tenant:read",
  "tenant:write",
  "users:read",
  "users:write",
  "hosts:read",
  "hosts:write",
  "branding:read",
  "branding:write",
  "settings:read",
  "settings:write",
  "devices:read",
  "devices:manage",
  "analytics:read",
  "audit:read"
];
var MERCHANT_READ = [
  "tenant:read",
  "hosts:read",
  "branding:read",
  "settings:read",
  "devices:read",
  "analytics:read"
];
var ROLE_PERMISSIONS = {
  SUPER_ADMIN: PERMISSIONS,
  MERCHANT_OWNER: [
    ...MERCHANT_READ,
    "tenant:write",
    "users:read",
    "users:write",
    "hosts:write",
    "branding:write",
    "settings:write",
    "devices:manage",
    "audit:read"
  ],
  MERCHANT_ADMIN: [
    ...MERCHANT_READ,
    "users:read",
    "hosts:write",
    "branding:write",
    "settings:write",
    "devices:manage",
    "audit:read"
  ],
  MERCHANT_SUPPORT: ["tenant:read", "devices:read", "devices:manage", "branding:read"],
  VIEWER: MERCHANT_READ
};
function hasPermission(role, permission) {
  return ROLE_PERMISSIONS[role].includes(permission);
}

// src/featured.ts
import { z as z12 } from "zod";
var CatalogSectionSchema = z12.enum(["live", "movie", "series"]);
var FEATURED_SECTIONS_MAX = 24;
var FEATURED_CATEGORIES_MAX = 20;
var FeaturedCategoryRefSchema = z12.object({
  section: CatalogSectionSchema,
  id: z12.string().trim().min(1).max(64),
  name: z12.string().trim().min(1).max(200)
});
var FeaturedSectionSchema = z12.object({
  id: z12.uuid(),
  name: z12.string(),
  logoUrl: z12.string().nullable(),
  categories: z12.array(FeaturedCategoryRefSchema),
  enabled: z12.boolean(),
  position: z12.number().int()
});
var categoryList = z12.array(FeaturedCategoryRefSchema).min(1).max(FEATURED_CATEGORIES_MAX).refine(
  (v) => new Set(v.map((c) => `${c.section}:${c.id}`)).size === v.length,
  "Duplicate category"
);
var CreateFeaturedSectionRequestSchema = z12.object({
  name: z12.string().trim().min(1).max(40),
  logoKey: z12.string().min(1).max(512).nullable().default(null),
  categories: categoryList,
  enabled: z12.boolean().default(true)
});
var UpdateFeaturedSectionRequestSchema = z12.object({
  name: z12.string().trim().min(1).max(40).optional(),
  logoKey: z12.string().min(1).max(512).nullable().optional(),
  categories: categoryList.optional(),
  enabled: z12.boolean().optional()
});
var ReorderFeaturedSectionsRequestSchema = z12.object({
  ids: z12.array(z12.uuid()).max(FEATURED_SECTIONS_MAX).refine((v) => new Set(v).size === v.length, "Duplicate section")
});
var FeaturedLogoUploadRequestSchema = z12.object({
  contentType: z12.enum(ASSET_CONTENT_TYPES),
  sizeBytes: z12.number().int().min(1).max(2 * 1024 * 1024)
});
var ProviderCategoriesRequestSchema = z12.object({ account: TestAccountSchema });
var CategoryOptionSchema = z12.object({ id: z12.string(), name: z12.string() });
var ProviderCategoriesSchema = z12.object({
  accountError: z12.enum(["INVALID_CREDENTIALS", "ACCOUNT_EXPIRED", "ACCOUNT_DISABLED"]).nullable(),
  live: z12.array(CategoryOptionSchema),
  movie: z12.array(CategoryOptionSchema),
  series: z12.array(CategoryOptionSchema)
});
var ClientFeaturedSectionSchema = z12.object({
  id: z12.uuid(),
  name: z12.string(),
  logoUrl: z12.string().nullable(),
  categories: z12.array(FeaturedCategoryRefSchema)
});
export {
  APK_PLATFORMS,
  ASSET_CONTENT_TYPES,
  ActivityReportSchema,
  AddFavoriteRequestSchema,
  AddReleaseTesterRequestSchema,
  AnnouncementSchema,
  ApiErrorSchema,
  AppVersionSchema,
  AssetCommitRequestSchema,
  AssetUploadRequestSchema,
  AssetUploadResponseSchema,
  AudienceSchema,
  AuditLogSchema,
  AuditQuerySchema,
  AuthTokensResponseSchema,
  BRAND_ASSET_KINDS,
  BrandAssetKindSchema,
  BrandColorsSchema,
  BrandMessagesSchema,
  BrandingSchema,
  BulkCreateHostsRequestSchema,
  BulkDeleteHostsRequestSchema,
  ButtonStyleSchema,
  CatalogListQuerySchema,
  CatalogSectionSchema,
  CategorySchema,
  ChallengeRequestSchema,
  ChangePasswordRequestSchema,
  ChannelSchema,
  ClientFeaturedSectionSchema,
  ClientInfoSchema,
  ClientLoginRequestSchema,
  ClientRefreshRequestSchema,
  ClientSessionResponseSchema,
  ContentTypeSchema,
  CreateAnnouncementRequestSchema,
  CreateFeaturedSectionRequestSchema,
  CreateHostRequestSchema,
  CreateHostUploadLinkRequestSchema,
  CreateTenantRequestSchema,
  CreateTenantUserRequestSchema,
  CustomerSessionSchema,
  DEFAULT_FEATURES,
  DeviceListQuerySchema,
  DeviceSchema,
  DeviceStatusSchema,
  DisableTwoFactorRequestSchema,
  DismissPilotRequestSchema,
  ERROR_CODES,
  EXPIRY_DAYS_PLACEHOLDER,
  EXPIRY_REMINDER_MAX_DAYS,
  EmailSchema,
  EpgProgramSchema,
  EpgQuerySchema,
  EpisodeSchema,
  ErrorCodeSchema,
  ExpiryReminderSchema,
  FEATURED_CATEGORIES_MAX,
  FEATURED_SECTIONS_MAX,
  FEATURE_KEYS,
  FavoriteSchema,
  FeatureKeySchema,
  FeaturedCategoryRefSchema,
  FeaturedLogoUploadRequestSchema,
  FeaturedSectionSchema,
  GlobalSettingsSchema,
  GrowthCountsSchema,
  GrowthReportSchema,
  HealthStatusSchema,
  HexColorSchema,
  HistoryItemSchema,
  HostAutoActionSchema,
  HostCheckLinkSchema,
  HostCheckPlanSchema,
  HostCheckReferenceRequestSchema,
  HostCheckReferenceSchema,
  HostCheckResultsRequestSchema,
  HostCountsSchema,
  HostHealthCheckSchema,
  HostMonitorSchema,
  HostProbeRequestSchema,
  HostProbeResultSchema,
  HostProviderRequestSchema,
  HostProviderSchema,
  HostRoleSchema,
  HostSchema,
  HostTestAllResultSchema,
  HostTestRequestSchema,
  HostTestResultSchema,
  HostUploadAddRequestSchema,
  HostUploadAddResultSchema,
  HostUploadLinkSchema,
  HostUploadPlanSchema,
  HostUploadReferenceRequestSchema,
  HostUsageSchema,
  HttpUrlSchema,
  IdParamSchema,
  LanguageSchema,
  LibraryHostSchema,
  LibraryItemMetaSchema,
  LibraryLinkSchema,
  LibraryProviderSchema,
  LocalCheckSchema,
  LocalCheckVerdictSchema,
  LocalizedTextSchema,
  LoginCountsSchema,
  LoginRequestSchema,
  LoginResponseSchema,
  MAX_BULK_HOSTS,
  MAX_HOST_PROVIDERS,
  MERCHANT_ROLES,
  MediaModeSchema,
  MerchantCodeResponseSchema,
  MerchantCodeSchema,
  MerchantRoleSchema,
  MinVersionPolicySchema,
  MovieDetailsSchema,
  MovieSchema,
  OkSchema,
  PERMISSIONS,
  PLATFORMS,
  PageQuerySchema,
  ParentalSettingsSchema,
  PasswordSchema,
  PilotReleaseSchema,
  PilotStateSchema,
  PlanCapabilitiesSchema,
  PlanLimitsSchema,
  PlanSchema,
  PlatformOverviewSchema,
  PlatformSchema,
  PlaybackTicketRequestSchema,
  PlaybackTicketSchema,
  PrincipalSchema,
  PromotePilotRequestSchema,
  ProviderAccountSchema,
  ProviderCategoriesRequestSchema,
  ProviderCategoriesSchema,
  ProviderHostsRequestSchema,
  ProviderKindSchema,
  PublicLatestReleaseSchema,
  PublishReleaseRequestSchema,
  ROLES,
  ROLE_PERMISSIONS,
  RecoveryCodesSchema,
  ReleaseChannelSchema,
  ReleaseSourceSchema,
  ReleaseTesterSchema,
  ReorderFeaturedSectionsRequestSchema,
  ResetMerchantCodeRequestSchema,
  ResolveQuerySchema,
  ResolveResponseSchema,
  RoleSchema,
  SearchQuerySchema,
  SearchResultSchema,
  SeasonSchema,
  SemverSchema,
  SendLibraryHostsRequestSchema,
  SendLibraryHostsResultSchema,
  SeriesDetailsSchema,
  SeriesSchema,
  SessionStatusSchema,
  SetHostMonitorRequestSchema,
  SetTenantStatusRequestSchema,
  StreamFormatSchema,
  SubscriptionStatusSchema,
  SupportInfoSchema,
  TV_WEB_PLATFORMS,
  TenantBundleSchema,
  TenantDetailSchema,
  TenantFeatureOverridesSchema,
  TenantSettingsSchema,
  TenantStatusSchema,
  TenantSummarySchema,
  TenantUsageSchema,
  TenantUserSchema,
  TestAccountSchema,
  TvAppRequestSchema,
  TvAppResponseSchema,
  TvBuildDecisionRequestSchema,
  TvBuildSchema,
  TvReleaseStateSchema,
  TwoFactorChallengeSchema,
  TwoFactorCodeRequestSchema,
  TwoFactorCodeSchema,
  TwoFactorSetupSchema,
  UpdateBrandingRequestSchema,
  UpdateFeaturedSectionRequestSchema,
  UpdateGlobalSettingsRequestSchema,
  UpdateHostRequestSchema,
  UpdatePolicySchema,
  UpdateTenantRequestSchema,
  UpdateTenantSettingsRequestSchema,
  UpdateTenantUserRequestSchema,
  UpsertAppVersionRequestSchema,
  UpsertPlanRequestSchema,
  UserStatusSchema,
  UuidSchema,
  VERSIONED_PLATFORMS,
  VerifiedLoginResponseSchema,
  VerifyChallengeRequestSchema,
  VersionPolicyResultSchema,
  WatchProgressRequestSchema,
  hasPermission,
  isApkPlatform,
  isTvWebPlatform,
  pageOf
};
